import React, { useEffect, useState } from "react";
import {
    Container,
    Row,
    Col,
    Card,
    Form,
    Button,
    Image,
    Spinner,
    Alert,
} from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import axios from "axios";

const ContactUsPage = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    const [errors, setErrors] = useState({});

    const [form, setForm] = useState({
        // meta_title: "",
        // meta_description: "",
        // meta_tags: "",
        banner_image: null,
        embedded_map_link: "",
        address: "",
        phone: "",
        email: "",
        open_time: "",
        facebook_link: "",
        instagram_link: "",
        x_link: "",
        tiktok_link: "",
        pintrest_link: "",
    });

    const [preview, setPreview] = useState(null);

    const getAuthHeaders = () => {
        const token = localStorage.getItem("authToken");
        return {
            Authorization: token ? `Bearer ${token}` : "",
        };
    };

    const getImageUrl = (path) => {
        if (!path) return "";
        if (path.startsWith("http://") || path.startsWith("https://")) return path;
        return `${process.env.REACT_APP_IMG_URL}/${path}`;
    };

    const getContactDetails = async () => {
        try {
            setLoading(true);
            setError("");

            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/web-manager/contact-us`,
                { headers: getAuthHeaders() }
            );

            const data = response.data?.data;

            if (data) {
                setForm({
                    // meta_title: data.meta_title || "",
                    // meta_description: data.meta_description || "",
                    // meta_tags: data.meta_tags || "",
                    banner_image: null,
                    embedded_map_link: data.embedded_map_link || "",
                    address: data.address || "",
                    phone: data.phone || "",
                    email: data.email || "",
                    open_time: data.open_time || "",
                    facebook_link: data.facebook_link || "",
                    instagram_link: data.instagram_link || "",
                    x_link: data.x_link || "",
                    tiktok_link: data.tiktok_link || "",
                    pintrest_link: data.pintrest_link || "",
                });

                if (data.banner_image) {
                    setPreview(getImageUrl(data.banner_image));
                }
            }
        } catch (err) {
            setError(err.response?.data?.message || "Failed to load contact information.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getContactDetails();
    }, []);

    const handleChange = (e) => {
        const { name, value, type, files } = e.target;

        if (type === "file") {
            const file = files?.[0] || null;
            setForm((prev) => ({ ...prev, banner_image: file }));

            if (file) {
                setPreview(URL.createObjectURL(file));
            }

            setErrors((prev) => ({ ...prev, banner_image: "" }));
            return;
        }

        setForm((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    // Helper function to convert existing image URL to a File Object for Laravel Validation
    const urlToFile = async (url, filename = "banner.png") => {
        const res = await fetch(url);
        const blob = await res.blob();
        const mimeType = blob.type || "image/png";
        return new File([blob], filename, { type: mimeType });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage("");
        setError("");

        try {
            setSaving(true);
            setErrors({});

            const formData = new FormData();

            // Commented SEO data falls back to dummy/default text so backend validation passes
            formData.append("meta_title", "Contact Us");
            formData.append("meta_description", "Contact Us Page");
            formData.append("meta_tags", "contact, support");

            // Handle Banner Image (If new file selected, use it. Otherwise convert existing preview URL to file)
            if (form.banner_image) {
                formData.append("banner_image", form.banner_image);
            } else if (preview) {
                try {
                    const existingFile = await urlToFile(preview, "existing_banner.png");
                    formData.append("banner_image", existingFile);
                } catch (e) {
                    console.error("Failed to re-attach existing image:", e);
                }
            }

            // Append all other form fields
            Object.keys(form).forEach((key) => {
                if (key !== "banner_image") {
                    formData.append(key, form[key] ?? "");
                }
            });

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/web-manager/contact-us`,
                formData,
                { headers: getAuthHeaders() }
            );

            setMessage(response.data?.message || "Contact details saved successfully.");
            await getContactDetails();
        } catch (err) {
            if (err.response?.status === 422) {
                setErrors(err.response.data.errors || {});
                setError(err.response.data.message || "Please fix validation errors.");
            } else {
                setError(err.response?.data?.message || "Something went wrong.");
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <PageLayout>
            <Container fluid className="py-4">
                <Row className="align-items-center mb-4">
                    <Col>
                        <h2 className="fw-bold mb-1">Contact Us Settings</h2>
                        <p className="text-muted mb-0">Manage contact details, banner, map, and social links.</p>
                    </Col>
                </Row>

                {message && (
                    <Alert variant="success" dismissible onClose={() => setMessage("")}>
                        {message}
                    </Alert>
                )}

                {error && (
                    <Alert variant="danger" dismissible onClose={() => setError("")}>
                        {error}
                    </Alert>
                )}

                {loading ? (
                    <div className="text-center py-5">
                        <Spinner animation="border" />
                        <div className="mt-3 text-muted">Loading details...</div>
                    </div>
                ) : (
                    <Form onSubmit={handleSubmit}>
                        {/* SEO Section Commented Out */}
                        {/* 
                        <Card className="border-0 shadow-sm mb-4">
                            <Card.Header className="bg-white py-3 fw-bold border-0">
                                SEO Metadata
                            </Card.Header>
                            <Card.Body>
                                <Row className="g-3">
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold small text-muted text-uppercase">Meta Title</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="meta_title"
                                                value={form.meta_title}
                                                onChange={handleChange}
                                                isInvalid={!!errors.meta_title}
                                                placeholder="Enter Meta Title"
                                                className="bg-light border-0 py-2 px-3"
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {Array.isArray(errors.meta_title) ? errors.meta_title[0] : errors.meta_title}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold small text-muted text-uppercase">Meta Tags</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="meta_tags"
                                                value={form.meta_tags}
                                                onChange={handleChange}
                                                isInvalid={!!errors.meta_tags}
                                                placeholder="e.g. contact, address, support"
                                                className="bg-light border-0 py-2 px-3"
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {Array.isArray(errors.meta_tags) ? errors.meta_tags[0] : errors.meta_tags}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={12}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold small text-muted text-uppercase">Meta Description</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={3}
                                                name="meta_description"
                                                value={form.meta_description}
                                                onChange={handleChange}
                                                isInvalid={!!errors.meta_description}
                                                placeholder="Enter Meta Description"
                                                className="bg-light border-0 py-2 px-3"
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {Array.isArray(errors.meta_description) ? errors.meta_description[0] : errors.meta_description}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card> 
                        */}

                        <Card className="border-0 shadow-sm mb-4">
                            <Card.Header className="bg-white py-3 fw-bold border-0">
                                Banner & General Details
                            </Card.Header>
                            <Card.Body>
                                <Row className="g-3">
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-semibold small text-muted text-uppercase">Banner Image</Form.Label>
                                            <Form.Control
                                                type="file"
                                                name="banner_image"
                                                accept=".jpg,.jpeg,.png,.webp"
                                                onChange={handleChange}
                                                isInvalid={!!errors.banner_image}
                                                className="bg-light border-0 py-2 px-3"
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {Array.isArray(errors.banner_image) ? errors.banner_image[0] : errors.banner_image}
                                            </Form.Control.Feedback>
                                        </Form.Group>

                                        {preview && (
                                            <Image
                                                src={preview}
                                                rounded
                                                className="w-100 shadow-sm"
                                                style={{ height: "180px", objectFit: "cover" }}
                                            />
                                        )}
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-semibold small text-muted text-uppercase">Embedded Map Link</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={6}
                                                name="embedded_map_link"
                                                value={form.embedded_map_link}
                                                onChange={handleChange}
                                                isInvalid={!!errors.embedded_map_link}
                                                placeholder="Enter Google Map Embed Iframe or URL"
                                                className="bg-light border-0 py-2 px-3"
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {Array.isArray(errors.embedded_map_link) ? errors.embedded_map_link[0] : errors.embedded_map_link}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold small text-muted text-uppercase">Email</Form.Label>
                                            <Form.Control
                                                type="email"
                                                name="email"
                                                value={form.email}
                                                onChange={handleChange}
                                                isInvalid={!!errors.email}
                                                placeholder="info@example.com"
                                                className="bg-light border-0 py-2 px-3"
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {Array.isArray(errors.email) ? errors.email[0] : errors.email}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold small text-muted text-uppercase">Phone</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="phone"
                                                value={form.phone}
                                                onChange={handleChange}
                                                isInvalid={!!errors.phone}
                                                placeholder="123456789"
                                                className="bg-light border-0 py-2 px-3"
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {Array.isArray(errors.phone) ? errors.phone[0] : errors.phone}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold small text-muted text-uppercase">Address</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="address"
                                                value={form.address}
                                                onChange={handleChange}
                                                isInvalid={!!errors.address}
                                                placeholder="Street, City, Country"
                                                className="bg-light border-0 py-2 px-3"
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {Array.isArray(errors.address) ? errors.address[0] : errors.address}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold small text-muted text-uppercase">Open Time</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="open_time"
                                                value={form.open_time}
                                                onChange={handleChange}
                                                isInvalid={!!errors.open_time}
                                                placeholder="e.g. Mon - Fri: 9:00 AM - 6:00 PM"
                                                className="bg-light border-0 py-2 px-3"
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {Array.isArray(errors.open_time) ? errors.open_time[0] : errors.open_time}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        <Card className="border-0 shadow-sm mb-4">
                            <Card.Header className="bg-white py-3 fw-bold border-0">
                                Social Links
                            </Card.Header>
                            <Card.Body>
                                <Row className="g-3">
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold small text-muted text-uppercase">Facebook</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="facebook_link"
                                                value={form.facebook_link}
                                                onChange={handleChange}
                                                isInvalid={!!errors.facebook_link}
                                                placeholder="https://facebook.com/..."
                                                className="bg-light border-0 py-2 px-3"
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {Array.isArray(errors.facebook_link) ? errors.facebook_link[0] : errors.facebook_link}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold small text-muted text-uppercase">Instagram</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="instagram_link"
                                                value={form.instagram_link}
                                                onChange={handleChange}
                                                isInvalid={!!errors.instagram_link}
                                                placeholder="https://instagram.com/..."
                                                className="bg-light border-0 py-2 px-3"
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {Array.isArray(errors.instagram_link) ? errors.instagram_link[0] : errors.instagram_link}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold small text-muted text-uppercase">X (Twitter)</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="x_link"
                                                value={form.x_link}
                                                onChange={handleChange}
                                                isInvalid={!!errors.x_link}
                                                placeholder="https://x.com/..."
                                                className="bg-light border-0 py-2 px-3"
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {Array.isArray(errors.x_link) ? errors.x_link[0] : errors.x_link}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold small text-muted text-uppercase">TikTok</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="tiktok_link"
                                                value={form.tiktok_link}
                                                onChange={handleChange}
                                                isInvalid={!!errors.tiktok_link}
                                                placeholder="https://tiktok.com/..."
                                                className="bg-light border-0 py-2 px-3"
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {Array.isArray(errors.tiktok_link) ? errors.tiktok_link[0] : errors.tiktok_link}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold small text-muted text-uppercase">Pinterest</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="pintrest_link"
                                                value={form.pintrest_link}
                                                onChange={handleChange}
                                                isInvalid={!!errors.pintrest_link}
                                                placeholder="https://pinterest.com/..."
                                                className="bg-light border-0 py-2 px-3"
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {Array.isArray(errors.pintrest_link) ? errors.pintrest_link[0] : errors.pintrest_link}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        <div className="d-flex justify-content-end mb-5">
                            <Button
                                type="submit"
                                variant="primary"
                                disabled={saving}
                                className="px-5 py-2 fw-semibold text-uppercase"
                            >   
                                {saving ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Saving...
                                    </>
                                ) : (
                                    "Save Settings"
                                )}
                            </Button>
                        </div>
                    </Form>
                )}
            </Container>
        </PageLayout>
    );
};

export default ContactUsPage;